import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { AppLayout } from './components/layout/AppLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import People from './pages/People';
import AddPerson from './pages/AddPerson';
import FamilyTree from './pages/FamilyTree';
import Timeline from './pages/Timeline';
import PersonPage from './pages/people/PersonPage/PersonPage';
import UserSettingsPage from './pages/settings/UserSettingsPage';
import UserProfilePage from './pages/user/UserProfilePage';
import { Search } from './pages/Search';
import SubmitRequest from './pages/feedback/SubmitRequest';
import MyRequests from './pages/feedback/MyRequests';
import RequestDetail from './pages/feedback/RequestDetail';
import FeedbackDashboardPage from './pages/dev/FeedbackDashboardPage';
import FeedbackAnalyticsPage from './pages/dev/FeedbackAnalyticsPage';
import DeveloperRequestDetailPage from './pages/dev/DeveloperRequestDetailPage';

// Admin Panel Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import UserDetail from './pages/admin/UserDetail';
import ApprovalQueue from './pages/admin/ApprovalQueue';
import ContentModeration from './pages/admin/ContentModeration';
import SystemConfig from './pages/admin/SystemConfig';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AuditLog from './pages/admin/AuditLog';
import { ChangeApproval } from './pages/admin/ChangeApproval';
import { AllChanges } from './pages/admin/AllChanges';
import ProfileCompletenessConfig from './pages/admin/ProfileCompletenessConfig';
import LinkRequestsPanel from './pages/admin/LinkRequestsPanel';

// Admin Notification Pages
import NotificationDashboard from './pages/admin/notifications/NotificationDashboard';
import NotificationTemplates from './pages/admin/notifications/NotificationTemplates';
import NotificationBroadcast from './pages/admin/notifications/NotificationBroadcast';
import NotificationAnalytics from './pages/admin/notifications/NotificationAnalytics';
import NotificationHistory from './pages/admin/notifications/NotificationHistory';
import NotificationSettings from './pages/admin/notifications/NotificationSettings';
import NotificationTesting from './pages/admin/notifications/NotificationTesting';

// Admin Mention Pages
import MentionAnalytics from './pages/admin/MentionAnalytics';
import MentionSettings from './pages/admin/MentionSettings';

// Admin Database Pages
import DatabaseTasks from './pages/admin/DatabaseTasks';

// Notification Pages
import NotificationCenter from './pages/NotificationCenter';
import NotificationPreferences from './pages/NotificationPreferences';

// Messaging Pages
import Messages from './pages/Messages';

// DEBUG ONLY - Remove before production
import { NotificationTester } from './components/debug/NotificationTester';

/**
 * Main Application Component
 * Production-ready genealogy application with authentication and routing
 */
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <MessagingProvider>
            <Router>
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
              <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginForm />} />
              <Route path="/register" element={<RegisterForm />} />

              {/* Debug Routes (REMOVE BEFORE PRODUCTION) */}
              <Route
                path="/debug/notifications"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <NotificationTester />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Feedback Routes (Public/Semi-Protected) */}
              <Route
                path="/feedback/submit"
                element={
                  <AppLayout>
                    <SubmitRequest />
                  </AppLayout>
                }
              />
              <Route
                path="/feedback/my-requests"
                element={
                  <AppLayout>
                    <MyRequests />
                  </AppLayout>
                }
              />
              <Route
                path="/feedback/request/:requestId"
                element={
                  <AppLayout>
                    <RequestDetail />
                  </AppLayout>
                }
              />

              {/* Developer Routes (Admin-Protected) */}
              <Route
                path="/dev/feedback-dashboard"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <FeedbackDashboardPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dev/feedback-analytics"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <FeedbackAnalyticsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dev/feedback/:requestId"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <DeveloperRequestDetailPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Panel Routes (Admin-Only) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <AdminDashboard />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <UserManagement />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <UserDetail />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/pending"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <ApprovalQueue />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/content"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <ContentModeration />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <SystemConfig />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <AdminAnalytics />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit-log"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <AuditLog />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/change-approval"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <ChangeApproval />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/changes"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <AllChanges />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/profile-completeness"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <ProfileCompletenessConfig />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/link-requests"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <LinkRequestsPanel />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/database"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <DatabaseTasks />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Notification Routes */}
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <NotificationDashboard />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/templates"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <NotificationTemplates />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/broadcast"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <NotificationBroadcast />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/analytics"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <NotificationAnalytics />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/history"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <NotificationHistory />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <NotificationSettings />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/testing"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <NotificationTesting />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Mention Routes */}
              <Route
                path="/admin/mentions/analytics"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <MentionAnalytics />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mentions/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout>
                      <MentionSettings />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Dashboard />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Dashboard />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Person Profile Routes - Migrated to /people path */}
              <Route
                path="/people/:personId"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <PersonPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/people/:personId/:tab"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <PersonPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* People and Tree Pages (Placeholders for Phase 2.6.2 and 2.6.3) */}
              <Route
                path="/people"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <People />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/people/add"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <AddPerson />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/tree/:personId?"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <FamilyTree />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/timeline"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Timeline />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/search"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Search />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/:tab?"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <UserSettingsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* User Public Profile */}
              <Route
                path="/user/:userId"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <UserProfilePage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Notification Routes */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <NotificationCenter />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications/preferences"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <NotificationPreferences />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Messaging Routes */}
              <Route
                path="/messages"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Messages />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages/:conversationId"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Messages />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              {/* Redirect old routes */}
              <Route path="/family" element={<Navigate to="/tree" replace />} />
              
              {/* 404 Not Found */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            </Box>
          </Router>
        </MessagingProvider>
      </NotificationProvider>
    </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
